/* -------------------------- Design imports start -------------------------- */
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import Button from '../../../components/Button'
import { AddRounded, DeleteRounded } from '@mui/icons-material'
import SearchSelectInput from '../../../components/inputs/SearchSelectInput'
import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'
import { Form, FormFieldType } from '../../Form'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { useTranslation } from 'react-i18next'
import LogTool from '../../../logger/logTools'
import { Contact, ContactGroup, RichContact, SelectOption } from '../../../utils/types'
import { useCreateContactGroup, useUpdateContactGroup } from '../hooks/useContactGroupMutations'
import { useEffect, useRef, useState } from 'react'
import useUniqueKey from '../../../hooks/useUniqueKey'
import { useAllContacts, useAllRichContacts } from '../hooks/useContactQueries'
import { getContactRepresentation, missingRequiredFormFields } from '../../../utils/functions'
import { useUserContext } from '../../../utils/context'
import { toast } from 'react-toastify'
import { buildRichContactGrouping } from '../utils/richContactHelpers'
import ContactDrawer from '../../Customer/ContactDrawer'
import { useCreateContact } from '../hooks/useContactMutations'
/* ------------------------- Functional imports end ------------------------- */

export type ContactListFieldObject = {
  key: string
  type: 'addContact' | 'selectContact' | 'savedContact'
  value: SelectOption | undefined
}

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: Partial<ContactGroup> | ContactGroup | undefined
    setState: React.Dispatch<React.SetStateAction<Partial<ContactGroup> | ContactGroup | undefined>>
  }
  onClose?: () => void
  onConfirm?: (input: Partial<ContactGroup>) => Promise<'SUCCESS' | 'ERROR'>
  enableEdit?: boolean
  onEditConfirm?: (input: Partial<ContactGroup>) => Promise<'SUCCESS' | 'ERROR'>
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function ContactGroupDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'ContactGroupDrawer', enable: true, logLevel: 'debug' })
  const {
    open,
    formState,
    setOpen,
    onClose = () => null,
    onConfirm,
    enableEdit = false,
    onEditConfirm,
  } = props

  const { t } = useTranslation()
  const { user } = useUserContext()
  const isGuestUser = user?.isGuestUser ?? false
  const uniqueFieldKey = useUniqueKey('contactListField')
  const createContactGroupMutation = useCreateContactGroup()
  const updateContactGroupMutation = useUpdateContactGroup()
  const createContactMutation = useCreateContact()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [dataLoaded, setDataLoaded] = useState(false)
  const [inEditMode, setInEditMode] = useState(false)
  const [openContactDrawer, setOpenContactDrawer] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const richContactStore = useRef<Record<string, RichContact[]>>({})
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [contactListForm, setContactListForm] = useState<ContactListFieldObject[]>([
    generateContactFieldObject('addContact'),
  ])
  // key of the AddressListFieldObject through that the addAddressOption callback was fired
  const [newContactReceiver, setNewContactReceiver] = useState<string | undefined>()
  const [contactDrawerFormInput, setContactDrawerFormInput] = useState<any>({})
  const isSaving = createContactGroupMutation.isPending || updateContactGroupMutation.isPending
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  // Disable addresses query for guest users - they don't have access to these resources
  const {
    data: richContactsData,
    isError: richContactsError,
    error: richContactsErrorObj,
  } = useAllRichContacts({}, !isGuestUser)

  useEffect(() => {
    if (richContactsError && richContactsErrorObj) {
      log.error('Error fetching contacts', richContactsErrorObj)
    }
  }, [richContactsError, richContactsErrorObj, log])

  useEffect(() => {
    if (!richContactsData) return
    const { store, options } = buildRichContactGrouping(richContactsData)
    richContactStore.current = store
    setContactOptions(options)
  }, [richContactsData])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Creates a new, unique AddressListFieldObject that can safely be inserted into the addressListForm.
   */
  function generateContactFieldObject(
    type: 'addContact' | 'selectContact' | 'savedContact' = 'selectContact',
    value?: SelectOption
  ): ContactListFieldObject {
    return {
      key: uniqueFieldKey.get(),
      type: type,
      value: value,
    }
  }

  /**
   * Determines if there are any empty address select input fields.
   * @returns
   */
  function missingContactSelectInput(): boolean {
    const selectFields = contactListForm.filter(fieldObj => fieldObj.type === 'selectContact')
    if (selectFields.length === 0) {
      return false
    }
    return selectFields.some(fieldObj => !fieldObj.value)
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false): void => {
    // reset modal to initial state
    formState.setState(undefined)
    uniqueFieldKey.reset()
    setContactListForm([generateContactFieldObject('addContact')])
    // Do not reset prevOpen to false here, as this might prevent re-initialization when opening again with same props
    // Instead, we rely on the open prop changing to trigger the useEffect/logic
    // setPrevOpen(false)
    setInEditMode(false)
    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleConfirm = async (): Promise<void> => {
    const contactGroupState = formState.state ?? {}
    const preparedContacts = contactListForm
      .filter(
        (fieldObj: ContactListFieldObject) =>
          (fieldObj.type === 'selectContact' || fieldObj.type === 'savedContact') &&
          typeof fieldObj.value !== 'undefined'
      )
      .map((fieldObj: ContactListFieldObject) =>
        fieldObj.value ? fieldObj.value.value : undefined
      )
      .filter((value): value is string => Boolean(value))

    const input: Partial<ContactGroup> = {
      ...contactGroupState,
      contacts: preparedContacts,
    }

    try {
      if (inEditMode || enableEdit) {
        if (onEditConfirm) {
          const status = await onEditConfirm(input)
          if (status === 'SUCCESS') {
            handleClose(true)
          }
          return
        }

        const idOrSelf = input.self ?? input.key
        if (!idOrSelf) {
          toast.error(
            t('contact:feedback.error.updateContactGroup', {
              defaultValue: 'Could not update contact group.',
            })
          )
          return
        }

        await updateContactGroupMutation.mutateAsync({ idOrSelf, data: input })
        toast.success(
          t('contact:feedback.success.updateContactGroup', {
            defaultValue: 'Contact updated successfully.',
          })
        )
        handleClose(true)
      } else {
        if (onConfirm) {
          const status = await onConfirm(input)
          if (status === 'SUCCESS') {
            handleClose(true)
          }
          return
        }
        await createContactGroupMutation.mutateAsync(input)
        toast.success(
          t('contact:feedback.success.createContactGroup', {
            defaultValue: 'Contact created successfully.',
          })
        )
        handleClose(true)
      }
    } catch (error) {
      log.error('Error saving contact', error)
      toast.error(
        t('contact:feedback.error.saveContactGroup', {
          defaultValue: 'Could not save contact. Please try again.',
        })
      )
    }
  }

  /**
   * Creates a new Contact instance in the backend and adds it to the availble
   * Contact select options.
   * @param contactInput
   */
  const handleAddContactOption = async (contactInput: Partial<Contact>) => {
    log.info('Begin creating new contact option', contactInput)
    const newContact = await createContactMutation.mutateAsync(contactInput)

    if (newContact) {
      // update states
      richContactStore.current.unrelated = richContactStore.current.unrelated
        ? [...richContactStore.current.unrelated, newContact as RichContact]
        : [newContact as RichContact]

      setContactOptions(prev => [
        ...prev,
        {
          value: newContact.self,
          labal: getContactRepresentation(newContact, 'unrelated'),
        },
      ])
      setContactListForm(prev => {
        const receiverFieldObj = prev.find(
          (fieldObj: ContactListFieldObject) => fieldObj.key === newContactReceiver
        )
        receiverFieldObj!.value = {
          value: newContact.self,
          label: getContactRepresentation(newContact, 'unrelated'),
        }
        return [...prev]
      })
      setNewContactReceiver(undefined)
      return 'SUCCESS'
    } else {
      log.error('Error creating address option')
      return 'ERROR'
    }
  }

  const handleAddContactToList = (): void => {
    setContactListForm((prev: any) => {
      const fieldObjIndex: number = prev.length - 1
      prev[fieldObjIndex] = generateContactFieldObject()
      return [...prev, generateContactFieldObject('addContact')]
    })
  }

  const handleRemoveContactFromList = (contactFieldObj: any): void => {
    log.debug('handleRemoveContactFromList', contactFieldObj)
    setContactListForm((prev: any) => {
      const i = prev.indexOf(contactFieldObj)
      // remove contactFieldObject
      prev.splice(i, 1)

      return [...prev]
    })
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const formFieldsContact = [
    {
      type: 'text',
      label: t('common:content.label.name'),
      key: 'name',
      required: true,
      validation: true,
      xl: 12,
      lg: 12,
      md: 12,
      sm: 12,
      xs: 12,
    },
  ] as FormFieldType[]

  const renderContactFieldObject = (contactFieldObj: ContactListFieldObject) => {
    if (contactFieldObj.type === 'addContact') {
      return (
        <ListItem key={contactFieldObj.key}>
          <ListItemIcon>
            <Tooltip title={t('contact:interaction.button.addContact')} placement="right" arrow>
              <Button
                startIcon={<AddRounded style={{ fontSize: '25px', color: 'GrayText' }} />}
                onClick={handleAddContactToList}
                style={{ fontSize: '16px', color: 'black' }}
              >
                {t('contact:interaction.button.addContact')}
              </Button>
            </Tooltip>
          </ListItemIcon>
        </ListItem>
      )
    } else if (contactFieldObj.type === 'savedContact') {
      return (
        <ListItem key={contactFieldObj.key}>
          <ListItemIcon>
            <IconButton onClick={() => handleRemoveContactFromList(contactFieldObj)}>
              <DeleteRounded />
            </IconButton>
          </ListItemIcon>
          <ListItemText primary={contactFieldObj.value?.label} />
        </ListItem>
      )
    } else if (contactFieldObj.type === 'selectContact') {
      const otherSelectedSelfs = contactListForm
        .filter(f => f.key !== contactFieldObj.key && f.type !== 'addContact' && Boolean(f.value))
        .map(f => f.value!.value)

      const currentFieldOptions = contactOptions.filter(
        opt => !otherSelectedSelfs.includes(opt.value)
      )

      return (
        <ListItem key={contactFieldObj.key}>
          <ListItemIcon>
            <IconButton onClick={() => handleRemoveContactFromList(contactFieldObj)}>
              <DeleteRounded />
            </IconButton>
          </ListItemIcon>
          <ListItemText>
            <SearchSelectInput
              name={t('common:content.label.contact')}
              fullWidth
              value={contactFieldObj.value ?? null}
              options={currentFieldOptions}
              onChange={(input: any) => {
                // update field object
                contactFieldObj.value = input
                // trigger rerender
                setContactListForm(prev => [...prev])
              }}
              onAddOption={() => {
                setNewContactReceiver(contactFieldObj.key)
                setOpenContactDrawer(true)
              }}
            />
          </ListItemText>
        </ListItem>
      )
    }
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  useEffect(() => {
    if (!open) {
      if (dataLoaded) {
        setDataLoaded(false)
      }
      return
    }

    if (!dataLoaded && formState.state) {
      // if the formState has a self or key property, it's an existing contact that should be edited
      // Empty objects with only empty string values (like emptyContact) should not trigger edit mode
      const isExistingContact = Boolean(formState.state.self || formState.state.key)
      setInEditMode(enableEdit || isExistingContact)

      if (isExistingContact) {
        if (Array.isArray(formState.state.contacts) && formState.state.contacts.length > 0) {
          const savedContacts = (formState.state.contacts as Array<Contact | string>).filter(
            (contact): contact is Contact =>
              typeof contact === 'object' && contact !== null && 'self' in contact
          )

          if (savedContacts.length > 0) {
            setContactListForm([
              ...savedContacts.map((contact: Contact) =>
                generateContactFieldObject('savedContact', {
                  value: contact.self,
                  label: getContactRepresentation(contact),
                })
              ),
              generateContactFieldObject('addContact'),
            ])
          } else {
            setContactListForm([generateContactFieldObject('addContact')])
          }
        } else {
          setContactListForm([generateContactFieldObject('addContact')])
        }
      }

      setDataLoaded(true)
    }
  }, [open, dataLoaded, formState.state, enableEdit])
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('openContactDrawer ->', openContactDrawer)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        size="big"
        title={
          inEditMode
            ? t('contact:interaction.button.editContactGroup')
            : t('contact:interaction.button.addContactGroup')
        }
        onClose={handleClose}
        confirmButtonProps={{
          text: inEditMode
            ? t('common:interaction.button.save')
            : t('common:interaction.button.add'),
          onConfirm: () => {
            void handleConfirm()
          },
          disabled:
            contactListForm.length < 2 || // there has to be at least one contact selected
            missingRequiredFormFields([...formFieldsContact], formState.state) ||
            missingContactSelectInput() ||
            isSaving,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('contact:content.label.groupInformation')}
        </Typography>
        <Grid spacing={2} container>
          <Form
            editing={true}
            formFields={formFieldsContact}
            formObject={formState.state ?? {}}
            onChange={(value: any) => formState.setState(value)}
          />
        </Grid>
        <Typography sx={{ mt: 4 }} variant="h6">
          {t('common:content.label.contacts')}
        </Typography>
        <List>
          {contactListForm.map((contactFieldObj: ContactListFieldObject) =>
            renderContactFieldObject(contactFieldObj)
          )}
        </List>
      </MultilevelDrawer>
      <ContactDrawer
        open={openContactDrawer}
        setOpen={setOpenContactDrawer}
        formState={{
          state: contactDrawerFormInput,
          setState: setContactDrawerFormInput,
        }}
        onConfirm={handleAddContactOption}
      />
    </>
  )
}
